/**
 * The Credit buckets screen's URL view state and sort columns (DNKADM-0430 P1-F).
 *
 * URL params on `/membership-credits/credit-buckets` (all optional; a default is never
 * written):
 * - `q`: search on the bucket name and its contact-system field key.
 * - `status`: the Active/Inactive/All filter (default `active`).
 * - `sort` + `dir`: the column sort. The server sends buckets in no defined order, so the
 *   default is Name ascending.
 */
import { creditUnitLabel, type CreditBucket } from '@/api/creditBuckets';
import { matchesSearch } from '@/components/list/listSearch';
import type { SortState, SortValue } from '@/components/list/listSort';

export const CREDIT_BUCKETS_URL_DEFAULTS = { q: '', status: 'active', sort: '', dir: '' };

export type CreditBucketSortKey = 'name' | 'fieldKey' | 'price' | 'unit' | 'status';
export const CREDIT_BUCKET_SORT_KEYS: readonly CreditBucketSortKey[] = [
  'name',
  'fieldKey',
  'price',
  'unit',
  'status',
];
export const CREDIT_BUCKET_DEFAULT_SORT: SortState<CreditBucketSortKey> = {
  key: 'name',
  dir: 'asc',
};

/** Display name for a bucket (its name, falling back to the id). */
export function bucketName(bucket: CreditBucket): string {
  return bucket.name === '' ? bucket.id : bucket.name;
}

/**
 * What a bucket row shows (and sorts by) in each column. A bucket with no field key or
 * no price ("Not sellable") sorts with the blanks (last).
 */
export function creditBucketSortValue(bucket: CreditBucket, key: CreditBucketSortKey): SortValue {
  switch (key) {
    case 'name':
      return bucketName(bucket);
    case 'fieldKey':
      return bucket.ghlCustomFieldKey;
    case 'price':
      return bucket.pricePerCredit;
    case 'unit':
      return creditUnitLabel(bucket.unit);
    case 'status':
      return bucket.isActive ? 'Active' : 'Inactive';
  }
}

/** The Credit buckets search: the name or the contact-system field key. */
export function creditBucketMatches(bucket: CreditBucket, query: string): boolean {
  return matchesSearch(query, [bucketName(bucket), bucket.ghlCustomFieldKey]);
}
